'use client';

import type { Category } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCategoryStore } from '@eliadmin/core/src/stores/category-store';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Paragraph } from '../ui/paragraph';
import CategorySort from '@eliadmin/core/src/components/categories/category-sort';
import { CategoryFilterSheet } from '@eliadmin/core/src/components/categories/category-filter-sheet';
import { AttributeType } from '@eliadmin/core/src/stores/category-store';
import { ProductCard, ProductCardSkeleton } from '../product/product-card';
import { Pagination } from '@eliadmin/core/src/components/ui/pagination';
import { Button } from '../ui/button';
import { CategoryFilterSelect } from './category-filter-select';
import { createCategoryProductsInfiniteQuery } from './category-products-query';
import {
  normalizeCategoryPage,
  parseCategoryFilterState,
} from '@eliadmin/core/src/components/categories/category-query-state';

export type CategoryProps = {
  className?: {
    grid_wrapper?: string;
    product?: string;
  };
  category: Category;
  onProductTotalChange?: (total: number) => void;
  paginationProps?: { scrollToElementAfterPageChange?: string };
  locale: string;
};

export function CategoryContent(props: CategoryProps) {
  const { me, isInitialized } = useCustomerStore();
  const authToken = me?.authToken;
  const filters = useCategoryStore((s) => s.filter);
  const paramsReady = useCategoryStore((s) => s.paramsReady);
  const category = useCategoryStore((s) => s.category);

  const selectAttributes = useMemo(
    () =>
      (category.attributes ?? [])
        .filter((a) => a.type === AttributeType.SELECT && a.name && a.values?.length)
        .slice(0, 3),
    [category.attributes],
  );
  const searchParams = useSearchParams();
  const queryClient = useQueryClient();

  const page = normalizeCategoryPage(searchParams.get('page'));
  const currentPage = page === 1 ? null : String(page);
  // CategoryParamsSync runs in an effect; SSR and first hydration already need URL filters.
  const search = searchParams.toString();
  const initialFilters = useMemo(
    () => parseCategoryFilterState(new URLSearchParams(search)).filter,
    [search],
  );
  const queryFilters = paramsReady ? filters : initialFilters;

  const { t } = useTranslations();

  const { queryOptions, queryWithoutPage } = useMemo(
    () =>
      createCategoryProductsInfiniteQuery({
        categoryId: props.category.id,
        locale: props.locale,
        currentPage,
        authToken,
        customerId: me?.customer.id,
        filters: queryFilters,
      }),
    [props.category.id, props.locale, currentPage, authToken, me?.customer.id, queryFilters],
  );

  const productsQuery = useInfiniteQuery({
    ...queryOptions,
    // Mount an already hydrated public query immediately: enabling it after /me
    // would refetch an aged ISR seed despite refetchOnMount: false. Unseeded and
    // customer-specific queries still wait for customer initialization.
    enabled:
      isInitialized || (!me && queryClient.getQueryData(queryOptions.queryKey) !== undefined),
  });

  const lastPage = productsQuery.data?.pages[productsQuery.data?.pages.length - 1];
  const flatList = productsQuery.data?.pages.flatMap((page) => page.data) ?? [];
  const [loadMode, setLoadMode] = useState<'manual' | 'auto'>('manual');
  const [fetchTrigger, setFetchTrigger] = useState<'load-more' | 'show-all' | 'auto' | null>(
    null,
  );
  const autoLoadRef = useRef<HTMLDivElement | null>(null);

  const fetchNextPageSafely = useCallback(
    async (trigger: 'load-more' | 'show-all' | 'auto') => {
      if (!productsQuery.hasNextPage) return false;
      if (productsQuery.isFetchingNextPage) return false;
      setFetchTrigger(trigger);
      try {
        await productsQuery.fetchNextPage();
        return true;
      } finally {
        setFetchTrigger(null);
      }
    },
    [productsQuery],
  );

  useEffect(() => {
    if (loadMode !== 'auto') return;
    if (!productsQuery.hasNextPage) return;

    const target = autoLoadRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;
        void fetchNextPageSafely('auto');
      },
      { root: null, rootMargin: '250px 0px', threshold: 0 },
    );

    observer.observe(target);

    return () => {
      observer.disconnect();
    };
  }, [fetchNextPageSafely, loadMode, productsQuery.hasNextPage]);

  useEffect(() => {
    setLoadMode('manual');
    setFetchTrigger(null);
  }, [currentPage, props.locale, authToken, queryWithoutPage, filters.attributes]);

  useEffect(() => {
    if (lastPage?.meta?.total === undefined) return;
    props.onProductTotalChange?.(lastPage.meta.total);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastPage?.meta?.total]);

  const hasProducts = flatList.length > 0;

  const sentinelRef = useRef<HTMLDivElement>(null);
  const [isStuck, setIsStuck] = useState(false);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const headerHeight = parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue('--header-height') || '0',
    );
    const observer = new IntersectionObserver(
      ([entry]) => setIsStuck(entry != null && !entry.isIntersecting),
      { rootMargin: `-${Math.ceil(headerHeight) + 1}px 0px 0px 0px`, threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  if ((!isInitialized && !productsQuery.data) || productsQuery.isPending) {
    return <CategoryContentSkeleton />;
  }

  return (
    <>
      <div ref={sentinelRef} className="h-0 w-full" />
      <section
        className={cn(
          'custom-section',
          'sticky z-[35] flex flex-col items-center justify-center gap-6 bg-background py-3',
          isStuck && 'shadow-[0px_8px_24px_0px_#0000001F]',
          'ea-category-content__actions-bar',
        )}
        style={{ top: 'calc(var(--header-height, 0px) + 0px)' }}
      >
        <div className={cn('custom-container', 'hidden items-center justify-between md:flex')}>
          <div className="flex items-center justify-start gap-3">
            <CategoryFilterSheet />
            <div className="hidden items-center gap-2 md:flex">
              {selectAttributes.map((attr) => (
                <CategoryFilterSelect key={attr.id} attribute={attr} />
              ))}
            </div>
          </div>
          <div className="flex items-center justify-end gap-6 xl:gap-12">
            <Paragraph color="muted-foreground" className="whitespace-nowrap">
              {productsQuery.data?.pages[0]?.meta?.total} {t('text3744')}
            </Paragraph>
            <CategorySort />
          </div>
        </div>
        <div className="flex w-full md:hidden">
          <CategoryFilterSheet />
        </div>
      </section>
      <div className={cn('custom-section', 'flex md:hidden')}>
        <CategorySort />
        <Paragraph color="muted-foreground" className="mt-6 whitespace-nowrap">
          {productsQuery.data?.pages[0]?.meta?.total} {t('text3744')}
        </Paragraph>
      </div>
      <section
        className={cn(
          'custom-section',
          'gap-6 pb-8 pt-8 sm:gap-8 sm:pb-10 sm:pt-10 md:gap-10 lg:gap-12 lg:pb-12 lg:pt-12',
        )}
      >
        <div
          className={cn('custom-container', 'flex items-start justify-between gap-4 lg:gap-6')}
        >
          <div className="grid w-full grid-cols-2 gap-x-2 gap-y-4 sm:gap-x-3 sm:gap-y-6 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {hasProducts ? (
              flatList.map((product) => <ProductCard product={product} key={product.slug} />)
            ) : (
              <Paragraph
                size="lg"
                color="muted-foreground"
                className="col-span-full mb-4 mt-12 text-center"
              >
                {t('text3687')}
              </Paragraph>
            )}
          </div>
        </div>
        {loadMode === 'manual' && !!productsQuery.hasNextPage && (
          <div
            className={cn(
              'flex flex-col justify-center gap-2 md:flex-row',
              'ea-category-products__load-more',
            )}
          >
            <Button
              variant="secondary"
              isLoading={productsQuery.isFetchingNextPage && fetchTrigger === 'load-more'}
              onClick={() => void fetchNextPageSafely('load-more')}
            >
              {t('text338')}
            </Button>
          </div>
        )}

        {loadMode === 'auto' && !!productsQuery.hasNextPage && (
          <div
            className={cn(
              'flex flex-col items-center justify-center gap-2',
              'ea-category-products__auto-load',
            )}
          >
            <div ref={autoLoadRef} className="h-1 w-full" />
            {!!productsQuery.isFetchingNextPage && (
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-muted border-t-foreground" />
            )}
          </div>
        )}
        <div className={cn('flex flex-row justify-center', 'ea-category-products__pagination')}>
          <Pagination
            links={lastPage?.meta.links}
            onPageClicked={(page) => {
              if (String(page) !== String(currentPage)) return;
              queryClient.resetQueries({
                queryKey: [
                  CacheKey.PRODUCTS,
                  'infinite',
                  currentPage,
                  queryWithoutPage,
                  me?.customer.id ?? null,
                  authToken,
                  props.locale,
                  filters.attributes,
                ],
                exact: true,
              });
            }}
            scrollToElementAfterPageChange={
              props.paginationProps?.scrollToElementAfterPageChange || undefined
            }
            resetWhen={[queryWithoutPage, filters.attributes]}
          />
        </div>
      </section>
    </>
  );
}

export function CategoryContentSkeleton() {
  return (
    <section className={cn('custom-section', 'animate-pulse', 'section-gap')}>
      <div className={cn('custom-container', 'hidden items-center justify-between sm:flex')}>
        <div className="flex items-center justify-start gap-3">
          <div className="h-12 w-[140px] rounded-[4px] bg-neutral-100" />
          <div className="h-12 w-[90px] rounded-[4px] bg-neutral-100" />
          <div className="h-12 w-[110px] rounded-[4px] bg-neutral-100" />
        </div>
        <div className="flex items-center justify-end gap-12">
          <div className="h-[18px] w-[105px] rounded-[4px] bg-neutral-100" />
          <div className="h-12 w-[175px] rounded-[4px] bg-neutral-100" />
        </div>
      </div>
      <div className="flex w-full flex-col items-center justify-center gap-3 sm:hidden">
        <div className="h-12 w-full bg-neutral-100" />
        <div className="h-12 w-full bg-neutral-100" />
        <div className="mt-3 h-4 w-[40%] bg-neutral-100" />
      </div>
      <div
        className={cn(
          'custom-container',
          'grid w-full grid-cols-2 gap-x-2 gap-y-4 sm:gap-x-3 sm:gap-y-6 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5',
        )}
      >
        <ProductCardSkeleton amount={10} variant="vertical" />
      </div>
    </section>
  );
}
